/**
 * Per-client printer role assignments (KV, not business payload).
 * Legacy receipt keys stay in sync with PRINT_ROLE.RECEIPT.
 */

import { kvGet, kvSet, kvRemove } from '../../infrastructure/storage/kvAccess';
import { isVirtualPrinterEntry } from '../../utils/printerAvailability';
import { findPrinterByDeviceIdLoose } from '../../utils/printerDeviceAliases';
import {
  PRINT_ROLE,
  PRINT_ROLE_IDS,
  normalizePrintRole,
  isPrintRole,
  roleAllowsReceiptFallback
} from './printerRoles';
import { notifyPrinterRolesChanged } from './printerRoleEvents';

export const PRINTER_ROLE_ASSIGNMENTS_KEY = 'aman:printerRoleAssignments';
export const LEGACY_PRINTER_DEVICE_KEY = 'printerDeviceName';
export const LEGACY_PRINTER_DISPLAY_KEY = 'printerDisplayName';

function sanitizeAssignment(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const deviceName = String(raw.deviceName || raw.name || '').trim();
  if (!deviceName) return null;
  if (isVirtualPrinterEntry({ name: deviceName, deviceName })) return null;
  const displayName = String(
    raw.displayName || raw.name || deviceName
  ).trim() || deviceName;
  return { deviceName, displayName };
}

function readRawMap() {
  try {
    const raw = kvGet(PRINTER_ROLE_ASSIGNMENTS_KEY);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return { ...raw };
  } catch {
    return {};
  }
}

function writeRawMap(map) {
  try {
    kvSet(PRINTER_ROLE_ASSIGNMENTS_KEY, map || {});
  } catch {
    /* ignore */
  }
}

function readLegacyReceiptAssignment() {
  try {
    const deviceName = String(
      kvGet(LEGACY_PRINTER_DEVICE_KEY) || ''
    ).trim();
    if (!deviceName) return null;
    if (isVirtualPrinterEntry({ name: deviceName, deviceName })) return null;
    const displayName =
      String(kvGet(LEGACY_PRINTER_DISPLAY_KEY) || '').trim() || deviceName;
    return { deviceName, displayName };
  } catch {
    return null;
  }
}

function writeLegacyReceipt(assignment) {
  try {
    if (!assignment?.deviceName) {
      kvRemove(LEGACY_PRINTER_DEVICE_KEY);
      kvRemove(LEGACY_PRINTER_DISPLAY_KEY);
      return;
    }
    kvSet(LEGACY_PRINTER_DEVICE_KEY, assignment.deviceName);
    kvSet(
      LEGACY_PRINTER_DISPLAY_KEY,
      assignment.displayName || assignment.deviceName
    );
  } catch {
    /* ignore */
  }
}

/**
 * All role → assignment entries (receipt falls back to legacy single-printer keys).
 * @returns {Record<string, { deviceName: string, displayName: string }|null>}
 */
export function getPrinterRoleAssignments() {
  const map = readRawMap();
  const out = {};
  for (const role of PRINT_ROLE_IDS) {
    out[role] = sanitizeAssignment(map[role]);
  }
  if (!out[PRINT_ROLE.RECEIPT]) {
    out[PRINT_ROLE.RECEIPT] = readLegacyReceiptAssignment();
  }
  return out;
}

/**
 * @param {string} role
 * @returns {{ deviceName: string, displayName: string }|null}
 */
export function getPrinterRoleAssignment(role) {
  const id = normalizePrintRole(role);
  return getPrinterRoleAssignments()[id] || null;
}

/**
 * @param {string} role
 * @param {{ deviceName?: string, name?: string, displayName?: string }|null} printer
 */
export function setPrinterRoleAssignment(role, printer) {
  if (!isPrintRole(role)) return { ok: false, error: 'invalid_role' };
  const id = normalizePrintRole(role);

  // Explicit clear (null / empty).
  if (printer == null || printer === '') {
    const map = readRawMap();
    delete map[id];
    writeRawMap(map);
    if (id === PRINT_ROLE.RECEIPT) writeLegacyReceipt(null);
    notifyPrinterRolesChanged({ role: id, action: 'clear', assignment: null });
    return { ok: true, role: id, assignment: null };
  }

  const cleaned = sanitizeAssignment(printer);
  if (!cleaned) {
    // Reject virtual / invalid devices without clearing an existing assignment.
    return { ok: false, error: 'virtual_or_invalid_printer', role: id };
  }

  const map = readRawMap();
  map[id] = cleaned;
  writeRawMap(map);
  if (id === PRINT_ROLE.RECEIPT) writeLegacyReceipt(cleaned);
  notifyPrinterRolesChanged({ role: id, action: 'set', assignment: cleaned });
  return { ok: true, role: id, assignment: cleaned };
}

/**
 * @param {string} role
 */
export function clearPrinterRoleAssignment(role) {
  return setPrinterRoleAssignment(role, null);
}

/**
 * Prefer role assignment; receipt fallback is opt-in only (never default for
 * document/label — avoids A4/labels on thermal POS paper).
 * Does not auto-pick Windows default (caller / resolvePrinterDeviceName owns that).
 *
 * @param {string} [role]
 * @param {{ allowLegacyReceiptFallback?: boolean }} [options]
 * @returns {string|null}
 */
export function resolveAssignedDeviceNameForRole(role, options = {}) {
  const id = normalizePrintRole(role, PRINT_ROLE.RECEIPT);
  const assigned = getPrinterRoleAssignment(id);
  if (assigned?.deviceName) return assigned.deviceName;

  const allowFallback =
    options.allowLegacyReceiptFallback === true ||
    (options.allowLegacyReceiptFallback !== false &&
      roleAllowsReceiptFallback(id));

  if (allowFallback && id !== PRINT_ROLE.RECEIPT) {
    const receipt = getPrinterRoleAssignment(PRINT_ROLE.RECEIPT);
    if (receipt?.deviceName) return receipt.deviceName;
  }
  return null;
}

/**
 * Effective target for a role (direct assignment; receipt fallback only if policy allows).
 * @param {string} role
 * @param {{ allowLegacyReceiptFallback?: boolean }} [options]
 * @returns {{
 *   deviceName: string,
 *   displayName: string,
 *   source: 'assigned'|'receipt_fallback'
 * }|null}
 */
export function resolveEffectivePrinterTarget(role, options = {}) {
  const id = normalizePrintRole(role, PRINT_ROLE.RECEIPT);
  const direct = getPrinterRoleAssignment(id);
  if (direct?.deviceName) {
    return {
      deviceName: direct.deviceName,
      displayName: direct.displayName || direct.deviceName,
      source: 'assigned'
    };
  }
  const allowFallback =
    options.allowLegacyReceiptFallback === true ||
    (options.allowLegacyReceiptFallback !== false &&
      roleAllowsReceiptFallback(id));
  if (allowFallback && id !== PRINT_ROLE.RECEIPT) {
    const receipt = getPrinterRoleAssignment(PRINT_ROLE.RECEIPT);
    if (receipt?.deviceName) {
      return {
        deviceName: receipt.deviceName,
        displayName: receipt.displayName || receipt.deviceName,
        source: 'receipt_fallback'
      };
    }
  }
  return null;
}

function matchPhysicalPrinter(list, deviceName) {
  if (!deviceName) return null;
  return findPrinterByDeviceIdLoose(list, deviceName);
}

function isOfflineMatch(match) {
  if (!match) return false;
  const statusRaw = String(match.status || match.options?.status || '').toLowerCase();
  return (
    statusRaw.includes('offline') ||
    statusRaw.includes('error') ||
    match.isOffline === true
  );
}

/**
 * Health snapshot for one role against a printer list (includes effective fallback).
 * @param {string} role
 * @param {Array<object>} physicalPrinters
 */
export function assessPrinterRoleHealth(role, physicalPrinters = []) {
  const id = normalizePrintRole(role);
  const list = Array.isArray(physicalPrinters) ? physicalPrinters : [];
  const direct = getPrinterRoleAssignment(id);
  const effective = resolveEffectivePrinterTarget(id);

  if (!effective) {
    return {
      role: id,
      assigned: false,
      online: false,
      deviceName: null,
      displayName: null,
      effectiveDeviceName: null,
      effectiveDisplayName: null,
      effectiveSource: null,
      status: 'unassigned'
    };
  }

  const match = matchPhysicalPrinter(list, effective.deviceName);
  const offline = isOfflineMatch(match);

  if (!match) {
    return {
      role: id,
      assigned: Boolean(direct),
      online: false,
      deviceName: direct?.deviceName || null,
      displayName: direct?.displayName || null,
      effectiveDeviceName: effective.deviceName,
      effectiveDisplayName: effective.displayName,
      effectiveSource: effective.source,
      status: 'missing'
    };
  }

  return {
    role: id,
    assigned: Boolean(direct),
    online: !offline,
    deviceName: direct?.deviceName || null,
    displayName: direct?.displayName || null,
    effectiveDeviceName: effective.deviceName,
    effectiveDisplayName:
      effective.displayName || match.displayName || match.name || effective.deviceName,
    effectiveSource: effective.source,
    status: offline
      ? 'offline'
      : effective.source === 'receipt_fallback'
        ? 'fallback_ok'
        : 'ok'
  };
}

/**
 * @param {Array<object>} physicalPrinters
 */
export function assessAllPrinterRolesHealth(physicalPrinters = []) {
  return PRINT_ROLE_IDS.map((role) =>
    assessPrinterRoleHealth(role, physicalPrinters)
  );
}

/**
 * Compact status for shell badges (receipt-centric, multi-role details).
 * @param {Array<object>} physicalPrinters
 */
export function summarizePrinterRolesHealth(physicalPrinters = []) {
  const roles = assessAllPrinterRolesHealth(physicalPrinters);
  const byRole = Object.fromEntries(roles.map((r) => [r.role, r]));
  const receipt = byRole[PRINT_ROLE.RECEIPT] || null;

  const ready = (h) =>
    h && (h.status === 'ok' || h.status === 'fallback_ok');
  const assignedOrFallback = roles.filter((h) => h.effectiveDeviceName);
  const allTargetsOnline =
    assignedOrFallback.length > 0 && assignedOrFallback.every(ready);
  const anyTargetOnline = assignedOrFallback.some(ready);
  const anyUnassigned = roles.some((h) => h.status === 'unassigned');
  const anyMissing = roles.some(
    (h) => h.status === 'missing' || h.status === 'offline'
  );
  const hasPhysical = filterPhysicalListHasAny(physicalPrinters);

  // Green only when the receipt *effective target* is known and present — not "any USB box".
  const receiptReady = ready(receipt);
  // Unconfigured till with physical queue: auto-print may work, but assignment is not confirmed.
  const unconfiguredPhysical =
    receipt?.status === 'unassigned' && hasPhysical;

  return {
    roles,
    byRole,
    connected: receiptReady,
    /** Auto path available but no explicit receipt assignment */
    unconfiguredPhysical: Boolean(unconfiguredPhysical),
    printerName:
      receipt?.effectiveDisplayName ||
      receipt?.displayName ||
      null,
    receiptStatus: receipt?.status || 'unassigned',
    allReady: allTargetsOnline && !anyMissing,
    partial: (anyTargetOnline && anyMissing) || unconfiguredPhysical,
    anyUnassigned,
    hasConfiguredTarget: assignedOrFallback.length > 0,
    hasPhysical
  };
}

/**
 * Plan for test / preview printing a role (transparent fallback messaging).
 * @param {string} role
 * @returns {{
 *   role: string,
 *   mode: 'direct'|'via_receipt'|'auto_discover',
 *   deviceName: string|null,
 *   displayName: string|null,
 *   willPrint: boolean
 * }}
 */
export function resolvePrinterRoleTestPlan(role) {
  const id = normalizePrintRole(role);
  const direct = getPrinterRoleAssignment(id);
  if (direct?.deviceName) {
    return {
      role: id,
      mode: 'direct',
      deviceName: direct.deviceName,
      displayName: direct.displayName || direct.deviceName,
      willPrint: true
    };
  }

  const effective = resolveEffectivePrinterTarget(id);
  if (effective?.source === 'receipt_fallback') {
    return {
      role: id,
      mode: 'via_receipt',
      deviceName: effective.deviceName,
      displayName: effective.displayName || effective.deviceName,
      willPrint: true
    };
  }

  return {
    role: id,
    mode: 'auto_discover',
    deviceName: null,
    displayName: null,
    willPrint: true
  };
}

function filterPhysicalListHasAny(physicalPrinters) {
  return Array.isArray(physicalPrinters) && physicalPrinters.length > 0;
}
